import type { FunkiSSEEvent } from '@/types/funki'
import { FunkiSSEEventType } from '@/types/funki'

interface ParseSSEResult {
  events: FunkiSSEEvent[]
  rest: string
}

const SSE_EVENT_PREFIX = 'event: ' // length 7
const SSE_DATA_PREFIX = 'data: ' // length 6

/**
 * Parse a raw SSE text buffer into typed events.
 * Incomplete frames are returned in `rest` for cross-chunk buffering.
 */
export function parseSSEChunk(buffer: string): ParseSSEResult {
  if (!buffer) return { events: [], rest: '' }

  const events: FunkiSSEEvent[] = []
  const frames = buffer.split(/\r?\n\r?\n/)
  const rest = frames.pop() ?? ''

  for (const frame of frames) {
    if (!frame.trim()) continue

    const lines = frame.split(/\r?\n/)
    let eventType = ''
    let dataLine = ''

    for (const line of lines) {
      if (line.startsWith(SSE_EVENT_PREFIX)) {
        eventType = line.slice(SSE_EVENT_PREFIX.length).trim()
      } else if (line.startsWith(SSE_DATA_PREFIX)) {
        dataLine = line
      }
    }

    if (eventType === FunkiSSEEventType.DONE) {
      events.push({ type: FunkiSSEEventType.DONE })
      continue
    }

    const rawData = dataLine.slice(SSE_DATA_PREFIX.length)

    try {
      const parsed = JSON.parse(rawData)
      events.push({ type: eventType, data: parsed } as FunkiSSEEvent)
    } catch {
      events.push({
        type: 'error',
        data: { reason: 'malformed', raw: dataLine },
      } as FunkiSSEEvent)
    }
  }

  return { events, rest }
}
